import { Skeleton } from "@mui/material";
import { ReactNode, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLoggedUserId, selectUserRole } from "../user/userSlice";
import { AccessDenied } from "../modules/ModuleAccessible";
import { loadMyPermissions, PackageAccess, packageAccessLevels, PackageName, selectPermissions } from "./permissionsSlice";

/**
 * The logged-in user's effective access to package `pkg`, as the backend computes it: super always
 * ReadWrite; a package the company hasn't got active None, admins included; admin ReadWrite;
 * plain users their company role's level. Undefined while the permissions are loading; None when
 * they failed to load.
 */
export const usePackageAccessState = (pkg: PackageName): PackageAccess | undefined => {
    const dispatch = useAppDispatch()
    const role = useAppSelector(selectUserRole)
    const userId = useAppSelector(selectLoggedUserId)
    const { status, userId: loadedFor, data } = useAppSelector(selectPermissions)

    const isSuper = role === "super"
    const needsLoad = !isSuper && !!userId && (loadedFor !== userId || status === "idle")

    useEffect(() => {
        if (needsLoad && userId && status !== "loading") {
            dispatch(loadMyPermissions(userId))
        }
    }, [dispatch, needsLoad, status, userId])

    if (isSuper) return "ReadWrite"
    if (status === "error") return "None"
    if (status !== "loaded" || loadedFor !== userId || !data) return undefined

    return data.permissions[pkg] ?? "None"
}

/** Like {@link usePackageAccessState}, but None while the permissions are loading. */
export const usePackageAccess = (pkg: PackageName): PackageAccess =>
    usePackageAccessState(pkg) ?? "None"

/** Whether the logged-in user has at least `access` to package `pkg`. */
export const usePackage = (pkg: PackageName, access: PackageAccess = "Read"): boolean => {
    const actual = usePackageAccess(pkg)
    return packageAccessLevels.indexOf(actual) >= packageAccessLevels.indexOf(access)
}

/** Renders its children only when the user has at least `access` to package `name`. */
export const Package = ({ name, access = "Read", children }: { name: PackageName, access?: PackageAccess, children: ReactNode }) => {
    const allowed = usePackage(name, access)
    return allowed ? <>{children}</> : null
}

/** Guards a whole page: a skeleton while the permissions load, then the page or "access denied". */
export const RequirePackage = ({ name, access = "Read", children }: { name: PackageName, access?: PackageAccess, children: ReactNode }) => {
    const actual = usePackageAccessState(name)
    if (actual === undefined) return <Skeleton variant="rectangular" height={200} />
    return packageAccessLevels.indexOf(actual) >= packageAccessLevels.indexOf(access) ? <>{children}</> : <AccessDenied />
}
